import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Search, BookOpen, AlertTriangle, CheckSquare, HelpCircle, Clock, FlaskConical, Library, ArrowRight } from "lucide-react";
import { GLOSSARY, RISKS, CHECKLIST_ITEMS, QUIZ_QUESTIONS, TIMELINE, SCENARIOS, CASE_STUDIES, DIAGNOSTIC_QUESTIONS } from "@/data/staticData";

const SECTION_META = {
  glossary: { label: "Glossaire", icon: BookOpen, path: "/dashboard/glossary", color: "bg-blue-50 text-blue-700" },
  risk: { label: "Classification", icon: AlertTriangle, path: "/dashboard/risks", color: "bg-amber-50 text-amber-700" },
  checklist: { label: "Checklist", icon: CheckSquare, path: "/dashboard/checklist", color: "bg-green-50 text-green-700" },
  quiz: { label: "Quiz", icon: HelpCircle, path: "/dashboard/quiz", color: "bg-purple-50 text-purple-700" },
  timeline: { label: "Timeline", icon: Clock, path: "/dashboard/timeline", color: "bg-slate-100 text-slate-700" },
  scenario: { label: "Simulateur", icon: FlaskConical, path: "/dashboard/simulator", color: "bg-orange-50 text-orange-700" },
  casestudy: { label: "Études de cas", icon: Library, path: "/dashboard/case-studies", color: "bg-teal-50 text-teal-700" },
};

function buildIndex() {
  const items = [];
  GLOSSARY.forEach(g => items.push({ section: "glossary", title: g.term, text: g.definition, keywords: `${g.term} ${g.definition}` }));
  RISKS.forEach(r => items.push({ section: "risk", title: `Risque ${r.level}`, text: r.description, keywords: `${r.level} ${r.description} ${r.rules.join(" ")} ${r.examples.join(" ")}` }));
  CHECKLIST_ITEMS.forEach(c => items.push({ section: "checklist", title: c.label, text: c.description, keywords: `${c.label} ${c.description} ${c.category}` }));
  QUIZ_QUESTIONS.forEach((q, i) => items.push({ section: "quiz", title: `Question ${i + 1}`, text: q.question, keywords: `${q.question} ${q.options.join(" ")} ${q.explanation}` }));
  TIMELINE.forEach(t => items.push({ section: "timeline", title: t.title, text: `${t.date} — ${t.description}`, keywords: `${t.date} ${t.title} ${t.description} ${t.impact} ${t.actions.join(" ")}` }));
  SCENARIOS.forEach(s => items.push({ section: "scenario", title: s.title, text: s.description, keywords: `${s.title} ${s.description} ${s.risk}` }));
  CASE_STUDIES.forEach(c => items.push({ section: "casestudy", title: c.title, text: c.description, keywords: `${c.title} ${c.description} ${c.regulations.join(" ")} ${c.actions.join(" ")} ${c.pitfalls.join(" ")}` }));
  return items;
}

function highlight(text, query) {
  if (!query || query.length < 2) return text;
  const parts = text.split(new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi"));
  return parts.map((part, i) =>
    part.toLowerCase() === query.toLowerCase()
      ? <mark key={i} className="bg-yellow-200 text-yellow-900 rounded px-0.5">{part}</mark>
      : part
  );
}

export default function SearchPage() {
  const [query, setQuery] = useState("");
  const navigate = useNavigate();
  const index = useMemo(() => buildIndex(), []);

  const results = useMemo(() => {
    if (query.length < 2) return [];
    const q = query.toLowerCase();
    return index
      .filter(item => item.keywords.toLowerCase().includes(q))
      .slice(0, 30);
  }, [query, index]);

  const grouped = useMemo(() => {
    const groups = {};
    results.forEach(r => {
      if (!groups[r.section]) groups[r.section] = [];
      groups[r.section].push(r);
    });
    return groups;
  }, [results]);

  const totalResults = results.length;

  return (
    <div className="p-6 max-w-3xl mx-auto" data-testid="search-page">
      <div className="mb-6 animate-fade-in">
        <div className="flex items-center gap-3 mb-2">
          <Search className="h-6 w-6 text-[var(--ia-navy)]" />
          <h1 className="text-2xl font-bold text-[var(--ia-text)]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Recherche globale</h1>
        </div>
        <p className="text-[var(--ia-text-muted)]">Recherchez dans l'ensemble du contenu : glossaire, risques, checklist, quiz, timeline, scénarios, études de cas</p>
      </div>

      <div className="relative mb-6 animate-fade-in">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-[var(--ia-muted)]" />
        <Input data-testid="global-search-input" className="pl-11 h-12 text-base" placeholder="Rechercher un terme, un concept, une obligation..." value={query} onChange={(e) => setQuery(e.target.value)} autoFocus />
      </div>

      {query.length >= 2 && (
        <p className="text-sm text-[var(--ia-muted)] mb-4" data-testid="search-results-count">
          {totalResults} résultat{totalResults !== 1 ? "s" : ""} trouvé{totalResults !== 1 ? "s" : ""}
        </p>
      )}

      {query.length < 2 && (
        <div className="text-center mt-16 animate-fade-in">
          <Search className="h-12 w-12 text-slate-200 mx-auto mb-4" />
          <p className="text-[var(--ia-muted)]">Tapez au moins 2 caractères pour lancer la recherche</p>
          <div className="flex flex-wrap gap-2 justify-center mt-6">
            {["RGPD", "AI Act", "DPO", "AIPD", "haut risque", "consentement", "transparence", "données"].map(tag => (
              <button key={tag} onClick={() => setQuery(tag)} data-testid={`search-tag-${tag}`}
                className="px-3 py-1.5 text-xs rounded-md bg-white border border-[var(--ia-border)] text-slate-600 hover:border-[var(--ia-blue)] hover:text-[var(--ia-blue)] transition-all">
                {tag}
              </button>
            ))}
          </div>
        </div>
      )}

      {Object.entries(grouped).map(([section, items]) => {
        const meta = SECTION_META[section];
        if (!meta) return null;
        const Icon = meta.icon;
        return (
          <div key={section} className="mb-6 animate-fade-in" data-testid={`search-group-${section}`}>
            <div className="flex items-center gap-2 mb-3">
              <Icon className="h-4 w-4 text-[var(--ia-muted)]" />
              <h2 className="text-sm font-semibold text-[var(--ia-text)]">{meta.label}</h2>
              <Badge variant="outline" className="text-[10px]">{items.length}</Badge>
            </div>
            <div className="space-y-2">
              {items.map((item, i) => (
                <Card key={i} className="border-[var(--ia-border)] cursor-pointer hover:-translate-y-px hover:shadow-sm transition-all"
                  onClick={() => navigate(meta.path)} data-testid={`search-result-${section}-${i}`}>
                  <CardContent className="p-4 flex items-start gap-3">
                    <Badge variant="outline" className={`text-[10px] flex-shrink-0 mt-0.5 ${meta.color}`}>{meta.label}</Badge>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-medium text-[var(--ia-text)] truncate">{highlight(item.title, query)}</h3>
                      <p className="text-xs text-[var(--ia-text-muted)] line-clamp-2 mt-0.5">{highlight(item.text, query)}</p>
                    </div>
                    <ArrowRight className="h-4 w-4 text-slate-300 flex-shrink-0 mt-1" />
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        );
      })}

      {query.length >= 2 && totalResults === 0 && (
        <div className="text-center mt-12 animate-fade-in">
          <Search className="h-10 w-10 text-slate-200 mx-auto mb-3" />
          <p className="text-[var(--ia-muted)]">Aucun résultat pour « {query} »</p>
          <p className="text-xs text-[var(--ia-muted)] mt-1">Essayez avec un terme plus général</p>
        </div>
      )}
    </div>
  );
}
